export type ExtraData = Record<string, any>;

export type StoredSurveyData<T> = {
  step: number;
  values: Partial<T>;
  reminder?: boolean;
  extraData: ExtraData;
};

const STORAGE_KEY = 'survey';
const getStorageKey = (prefix?: string) =>
  prefix ? `${prefix}_${STORAGE_KEY}` : STORAGE_KEY;
const STORAGE_OPTIMIZE_KEY = 'optimizeVariants';

type OptimizeVariants = Record<string, any> | undefined;
type OptimizeStorageGetParams = {
  clearOnFail?: boolean;
};

const optimizeStorage = {
  get: ({ clearOnFail = true }: OptimizeStorageGetParams = {}) => {
    let optimizeVariants: OptimizeVariants;

    try {
      optimizeVariants = JSON.parse(
        window.sessionStorage.getItem(STORAGE_OPTIMIZE_KEY) || '',
      );
    } catch (e: any) {
      if (clearOnFail) {
        window.sessionStorage.removeItem(STORAGE_OPTIMIZE_KEY);
      }
    }

    return optimizeVariants;
  },
  set: (optimizeVariants: OptimizeVariants) => {
    window.sessionStorage.setItem(
      STORAGE_OPTIMIZE_KEY,
      JSON.stringify(optimizeVariants),
    );
  },
};

export const loadData = <T extends Record<string, any>>(prefix?: string) => {
  if (typeof window === 'undefined') return;

  const key = getStorageKey(prefix);
  const rawData = window.localStorage.getItem(key);

  let data: StoredSurveyData<T> = { step: 0, values: {}, extraData: {} };

  try {
    if (rawData) {
      const loadedData = JSON.parse(rawData);

      // Ensure we are not retuning something like a string or number
      if (typeof loadedData === 'object') {
        data = loadedData;
      } else {
        throw new Error('Stored survey data is invalid');
      }
    }
  } catch (e: any) {
    // Data in storage is invalid
    window.localStorage.removeItem(key);
  }

  const optimizeVariants = optimizeStorage.get();

  // Make sure optimize variants are up to date
  if (optimizeVariants) {
    data.values = {
      ...data.values,
      optimizeVariants,
    };
  }

  return data;
};

export const saveData = <T = Record<string, any>>(
  data: Partial<T>,
  prefix?: string,
) => {
  if (typeof window === 'undefined') return;

  const current = loadData(prefix) || {};

  const key = getStorageKey(prefix);
  window.localStorage.setItem(key, JSON.stringify({ ...current, ...data }));
};

export const reminderToggle = (visibility: boolean, prefix?: string) => {
  if (typeof window === 'undefined') return;

  const current = loadData(prefix) || {};

  const key = getStorageKey(prefix);
  window.localStorage.setItem(
    key,
    JSON.stringify({ ...current, reminder: visibility }),
  );
};

export const reminderVisibility = (prefix?: string) => {
  if (typeof window === 'undefined') return false;

  const key = getStorageKey(prefix);
  const rawData = window.localStorage.getItem(key);

  if (!rawData) return false;

  try {
    const value = JSON.parse(rawData);
    return value.reminder;
  } catch (e: any) {
    // Data in storage is invalid
    window.localStorage.removeItem(key);
    return false;
  }
};

export const clearData = (prefix?: string) => {
  if (typeof window === 'undefined') return;

  const key = getStorageKey(prefix);
  window.localStorage.removeItem(key);
};

export const saveOptimizeVariants = (optimizeVariants: Record<string, any>) => {
  let mergedVariants = optimizeVariants;

  const savedVariants = optimizeStorage.get();

  if (savedVariants && typeof savedVariants === 'object') {
    mergedVariants = {
      ...savedVariants,
      ...mergedVariants,
    };
  }

  // Preserve optimize variants so that surveys can load them
  optimizeStorage.set(mergedVariants);
};
